import { useEffect, useRef, useState } from "react";
import { Head, Link, router, useForm, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import AppLayout from "@/layouts/app_layout";
import { FormContent, FormFields, PageCanvas } from "@/components/page-canvas";
import { FormErrors, errorText } from "@/components/forms/form-errors";
import VehicleFields, {
  vehicleErrorTargets,
  type VehicleValues,
} from "@/components/forms/vehicle-fields";
import VehiclePhotos from "@/components/forms/vehicle-photos";
import {
  CatalogueCredit,
  type VehicleCatalogue,
} from "@/pages/vehicles/catalogue-fields";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { FieldSet, FieldLegend } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import {
  locationRequestsPath,
  locationRequestPath,
  editLocationRequestPath,
  newLocationRequestPath,
  locationRequestCustomerPath,
  locationCustomerPath,
  locationVehiclePath,
} from "@/routes";
import { Choice } from "./controls";
import type { Enquiry, Pagination } from "./types";
import {
  forgetEnquiryDraft,
  rememberEnquiryDraft,
  restoreEnquiryDraft,
  reconcileEnquiryProfileRevisions,
  type EnquiryDraft,
} from "./enquiry-draft";

type Customer = {
  id: string;
  revision: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
};
type Vehicle = {
  id: string;
  revision: string;
  make: string;
  model: string;
  registration_number?: string | null;
};
type Match = {
  id: string;
  revision: string;
  name: string;
  matched_contacts: string[];
};
export type EnquiryEditorProps = {
  enquiry?: Enquiry | null;
  customer?: Customer | null;
  vehicle?: Vehicle | null;
  draft_id?: string;
  catalogue: VehicleCatalogue;
  matches?: Match[];
  customer_candidates?: Customer[];
  customer_pagination?: Pagination;
  customer_query?: string;
  vehicle_candidates?: Vehicle[];
  vehicle_pagination?: Pagination;
  vehicle_query?: string;
  origin?: "requests" | "customer" | "vehicle";
  origin_id?: string | null;
  back_path?: string;
};
const blankPage = { page: 1, pages: 1 };

export default function EnquiryEditor({
  enquiry = null,
  customer,
  vehicle = null,
  draft_id = "new",
  catalogue,
  matches = [],
  customer_candidates = [],
  customer_pagination = blankPage,
  customer_query = "",
  vehicle_candidates = [],
  vehicle_pagination = blankPage,
  vehicle_query = "",
  origin = "requests",
  origin_id,
  back_path,
}: EnquiryEditorProps) {
  const { auth } = usePage().props;
  const { t } = useTranslation();
  const locationId = auth.location?.id || "";
  const knownCustomer = customer === undefined ? enquiry?.customer : customer;
  const knownVehicle =
    vehicle ||
    (knownCustomer?.id === enquiry?.customer?.id ? enquiry?.vehicle : null);
  const details = enquiry?.intake_details || {};
  const draftIdentity = draft_id === "new" ? enquiry?.id || draft_id : draft_id;
  const key = `${auth.account?.id}:${auth.user?.id}:${locationId}:${draftIdentity}`;
  const revision = enquiry?.revision || "new";
  const initial: EnquiryDraft = {
    customer_id: knownCustomer?.id || "",
    customer_revision: knownCustomer?.revision || "",
    vehicle_id: knownVehicle?.id || "",
    vehicle_revision: knownVehicle?.revision || "",
    name: knownCustomer?.name || String(details.name || ""),
    email: knownCustomer?.email || String(details.email || ""),
    phone: knownCustomer?.phone || String(details.phone || ""),
    address: knownCustomer?.address || String(details.address || ""),
    make: knownVehicle?.make || String(details.make || ""),
    model: knownVehicle?.model || String(details.model || ""),
    fabrication_year: String(
      enquiry?.vehicle?.fabrication_year || details.fabrication_year || "",
    ),
    colour: enquiry?.vehicle?.colour || String(details.colour || ""),
    registration_number:
      knownVehicle?.registration_number ||
      String(details.registration_number || ""),
    vin: enquiry?.vehicle?.vin || String(details.vin || ""),
    channel: enquiry?.channel || "manual",
    channel_identity: enquiry?.channel_identity || "",
    body: enquiry?.body || "",
    service_details: enquiry?.service_details || "",
    photo_ids: (enquiry?.vehicle_photos || []).map((photo) => photo.id),
    files: [],
  };
  const form = useForm<EnquiryDraft>(`enquiry:${key}`, initial);
  const { setData } = form;
  const [ready, setReady] = useState(false);
  const [restored, setRestored] = useState(false);
  const completed = useRef(false);
  const [customerQuery, setCustomerQuery] = useState(customer_query);
  const [vehicleQuery, setVehicleQuery] = useState(vehicle_query);
  useEffect(() => {
    const recovered = restoreEnquiryDraft(
      key,
      revision,
      initial,
      !!enquiry && draftIdentity !== enquiry.id,
    );
    setRestored(JSON.stringify(recovered) !== JSON.stringify(form.data));
    setData(recovered);
    setReady(true);
    // Restore once for this scoped editor; server updates keep live staged input.
  }, [key]);
  const profileRevisions = JSON.stringify({
    customers: [knownCustomer, ...customer_candidates, ...matches]
      .filter((profile): profile is Customer | Match => !!profile)
      .map(({ id, revision }) => ({ id, revision })),
    vehicles: [knownVehicle, ...vehicle_candidates]
      .filter((profile): profile is Vehicle => !!profile)
      .map(({ id, revision }) => ({ id, revision })),
  });
  useEffect(() => {
    const current = JSON.parse(profileRevisions) as {
      customers: { id: string; revision: string }[];
      vehicles: { id: string; revision: string }[];
    };
    // A rejected save redisplays current profile facts. Refresh only the selected
    // profiles' concurrency tokens; staged input and other selections stay intact.
    setData((data) =>
      reconcileEnquiryProfileRevisions(
        data,
        current.customers,
        current.vehicles,
      ),
    );
  }, [key, profileRevisions, setData]);
  useEffect(() => {
    if (ready && !completed.current)
      rememberEnquiryDraft(key, revision, form.data);
  }, [key, revision, form.data, ready]);
  useEffect(() => {
    if (enquiry || draft_id === "new") return;
    const address = new URL(window.location.href);
    if (
      address.searchParams.get("draft_id") === draft_id &&
      address.searchParams.get("origin") === origin &&
      address.searchParams.get("origin_id") === (origin_id || null)
    )
      return;
    address.searchParams.set("draft_id", draft_id);
    address.searchParams.set("origin", origin);
    if (origin_id) address.searchParams.set("origin_id", origin_id);
    else address.searchParams.delete("origin_id");
    router.replace({
      url: `${address.pathname}${address.search}`,
      preserveState: true,
      preserveScroll: true,
    });
  }, [draft_id, enquiry, origin, origin_id]);
  if (!auth.location) return null;

  const customers = [knownCustomer, ...customer_candidates]
    .filter((choice): choice is Customer => !!choice)
    .filter(
      (choice, index, choices) =>
        choices.findIndex((other) => other.id === choice.id) === index,
    );
  const selectedCustomer = customers.find(
    (choice) => choice.id === form.data.customer_id,
  );
  const selectedMatch = matches.find(
    (choice) => choice.id === form.data.customer_id,
  );
  const needsAssociation =
    !!selectedMatch &&
    enquiry?.customer?.id !== selectedMatch.id &&
    (!!enquiry || knownCustomer?.id !== selectedMatch.id);
  const representedVehicle =
    !enquiry || enquiry.customer?.id === form.data.customer_id
      ? knownVehicle
      : null;
  const vehicles = [representedVehicle, ...vehicle_candidates]
    .filter((choice): choice is Vehicle => !!choice)
    .filter(
      (choice, index, choices) =>
        choices.findIndex((other) => other.id === choice.id) === index,
    );
  const selectedVehicle = vehicles.find(
    (choice) => choice.id === form.data.vehicle_id,
  );
  const errors = Object.fromEntries(
    Object.entries(form.errors).map(([field, value]) => [
      field,
      errorText(value),
    ]),
  );
  const customerError = (field: string) =>
    errors[`customer.${field}`] || errors[field];
  const vehicleErrors = Object.fromEntries(
    [...Object.keys(vehicleErrorTargets()), "files"].map((field) => [
      field,
      errors[`vehicle.${field}`] || errors[field] || "",
    ]),
  );
  const targets = {
    channel_identity: "request-identity",
    body: "request-body",
    service_details: "request-service-details",
    ...Object.fromEntries(
      ["name", "email", "phone", "address"].flatMap((field) => [
        [field, `request-${field}`],
        [`customer.${field}`, `request-${field}`],
      ]),
    ),
    ...Object.fromEntries(
      Object.entries(vehicleErrorTargets("request")).flatMap(([field, id]) => [
        [field, id],
        [`vehicle.${field}`, id],
      ]),
    ),
    "vehicle.files": "request-files",
    files: "request-files",
  };
  const title = t(enquiry ? "requests.complete_details" : "requests.add");
  const backPath = enquiry
    ? locationRequestPath(locationId, enquiry.id)
    : back_path ||
      (vehicle
        ? locationVehiclePath(locationId, vehicle.id)
        : customer
          ? locationCustomerPath(locationId, customer.id)
          : locationRequestsPath(locationId));
  const navigate = (values: Record<string, string | number>) => {
    const query = {
      draft_id,
      origin,
      origin_id: origin_id || undefined,
      customer_query: customerQuery,
      vehicle_query: vehicleQuery,
      ...(enquiry
        ? { selected_customer_id: form.data.customer_id || "new" }
        : { customer_id: form.data.customer_id || undefined }),
      ...values,
    };
    router.get(
      enquiry
        ? editLocationRequestPath(locationId, enquiry.id)
        : newLocationRequestPath(locationId),
      query,
      { preserveState: true, preserveScroll: true },
    );
  };
  function chooseCustomer(id: string) {
    const chosen = customers.find((choice) => choice.id === id);
    setData((data) => ({
      ...data,
      customer_id: chosen?.id || "",
      customer_revision: chosen?.revision || "",
      vehicle_id: "",
      vehicle_revision: "",
    }));
    navigate(
      enquiry
        ? { selected_customer_id: chosen?.id || "new", vehicle_page: 1 }
        : { customer_id: chosen?.id || "", vehicle_page: 1 },
    );
  }
  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    rememberEnquiryDraft(key, revision, form.data);
    form.transform((data) => {
      const request = {
        channel: data.channel,
        channel_identity: data.channel_identity,
        body: data.body,
        service_details: data.service_details,
        customer_id: data.customer_id,
        customer_revision: data.customer_revision,
        customer: {
          name: data.name,
          email: data.email,
          phone: data.phone,
          address: data.address,
        },
        ...(!needsAssociation
          ? {
              vehicle_id: data.vehicle_id,
              vehicle_revision: data.vehicle_revision,
              vehicle: {
                make: data.make,
                model: data.model,
                fabrication_year: data.fabrication_year,
                colour: data.colour,
                registration_number: data.registration_number,
                vin: data.vin,
                files: data.files,
                photo_ids: data.photo_ids.length ? data.photo_ids : [""],
              },
            }
          : {}),
      };
      return {
        revision,
        draft_id,
        continue_details: needsAssociation ? "1" : "0",
        origin,
        origin_id: origin_id || undefined,
        customer_query: customerQuery,
        customer_page: customer_pagination.page,
        vehicle_query: vehicleQuery,
        vehicle_page: vehicle_pagination.page,
        request,
      };
    });
    const options = {
      preserveScroll: true,
      forceFormData: form.data.files.length > 0,
      onSuccess: () => {
        if (needsAssociation) return;
        completed.current = true;
        forgetEnquiryDraft(key);
        form.resetAndClearErrors();
      },
    };
    if (enquiry && needsAssociation)
      form.post(locationRequestCustomerPath(locationId, enquiry.id), options);
    else if (enquiry)
      form.patch(locationRequestPath(locationId, enquiry.id), options);
    else form.post(locationRequestsPath(locationId), options);
  }
  const pagination = (kind: "customer" | "vehicle", page: Pagination) =>
    page.pages > 1 && (
      <nav
        aria-label={t("requests.pages")}
        className="flex flex-wrap items-center gap-3"
      >
        {page.page > 1 && (
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate({ [`${kind}_page`]: page.page - 1 })}
          >
            {t("requests.previous")}
          </Button>
        )}
        <span>{t("requests.page", page)}</span>
        {page.page < page.pages && (
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate({ [`${kind}_page`]: page.page + 1 })}
          >
            {t("requests.next")}
          </Button>
        )}
      </nav>
    );
  return (
    <AppLayout
      breadcrumbs={[
        { title: t("requests.title"), href: locationRequestsPath(locationId) },
      ]}
    >
      <Head title={title} />
      <PageCanvas>
        <FormContent>
          <h1 className="text-page font-display">{title}</h1>
          <p className="text-muted-foreground">{t("requests.partial_help")}</p>
        </FormContent>
        <Card
          data-layout-frame={enquiry ? "request-completion" : "request-intake"}
        >
          <CardContent className="pt-6">
            <FormContent>
              <form
                onSubmit={submit}
                className="space-y-6"
                encType="multipart/form-data"
              >
                <FormErrors errors={errors} targets={targets} />
                {restored && (
                  <p role="status" className="text-muted-foreground text-sm">
                    {t("requests.draft_restored")}
                  </p>
                )}
                <FieldSet>
                  <FieldLegend variant="section">
                    {t("customers.details")}
                  </FieldLegend>
                  <p className="text-sm text-muted-foreground">
                    {t("requests.staged_help")}
                  </p>
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="min-w-0 flex-1 space-y-2">
                      <Label htmlFor="request-customer-search">
                        {t("requests.search_customers")}
                      </Label>
                      <Input
                        id="request-customer-search"
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            if (!event.nativeEvent.isComposing)
                              navigate({ customer_page: 1 });
                          }
                        }}
                        value={customerQuery}
                        onChange={(event) =>
                          setCustomerQuery(event.target.value)
                        }
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate({ customer_page: 1 })}
                    >
                      {t("requests.filter")}
                    </Button>
                  </div>
                  <Choice
                    id="request-customer-select"
                    name="customer_selection"
                    label={t("requests.choose_customer")}
                    value={selectedCustomer?.id || "new"}
                    onValueChange={(value) => chooseCustomer(value || "new")}
                    options={[
                      { id: "new", name: t("requests.new_customer") },
                      ...customers.map((choice) => ({
                        id: choice.id,
                        name: choice.name,
                      })),
                    ]}
                  />
                  {pagination("customer", customer_pagination)}
                  {matches.length > 0 && (
                    <div className="space-y-3">
                      <p>{t("customers.reuse_help")}</p>
                      <Choice
                        id="request-customer-choice"
                        name="customer_match"
                        label={t("customers.choose")}
                        value={selectedMatch?.id || "new"}
                        onValueChange={(value) => {
                          const match = matches.find(
                            (choice) => choice.id === value,
                          );
                          setData((data) => ({
                            ...data,
                            customer_id: match?.id || "",
                            customer_revision: match?.revision || "",
                            vehicle_id: "",
                            vehicle_revision: "",
                          }));
                        }}
                        options={[
                          { id: "new", name: t("requests.correct_contacts") },
                          ...matches.map((match) => ({
                            id: match.id,
                            name: `${match.name} (${match.matched_contacts.map((contact) => t(`customers.${contact}`)).join(", ")})`,
                          })),
                        ]}
                      />
                    </div>
                  )}
                  <FormFields paired={false}>
                    {(["name", "email", "phone", "address"] as const).map(
                      (field) => (
                        <div key={field} className="space-y-2">
                          <Label htmlFor={`request-${field}`}>
                            {t(`customers.${field}`)}
                          </Label>
                          <Input
                            id={`request-${field}`}
                            name={`request[customer][${field}]`}
                            type={
                              field === "email"
                                ? "email"
                                : field === "phone"
                                  ? "tel"
                                  : "text"
                            }
                            value={
                              selectedCustomer?.[field] ?? form.data[field]
                            }
                            onChange={(event) =>
                              setData(field, event.target.value)
                            }
                            readOnly={!!selectedCustomer}
                            autoComplete={
                              field === "phone"
                                ? "tel"
                                : field === "address"
                                  ? "street-address"
                                  : field
                            }
                            aria-invalid={!!customerError(field)}
                            aria-describedby={
                              customerError(field)
                                ? `request-${field}-error`
                                : field === "phone"
                                  ? "request-phone-help"
                                  : undefined
                            }
                          />
                          {field === "phone" && (
                            <p
                              id="request-phone-help"
                              className="text-sm text-muted-foreground"
                            >
                              {t("customers.phone_help")}
                            </p>
                          )}
                          {customerError(field) && (
                            <p
                              id={`request-${field}-error`}
                              className="text-sm text-destructive"
                            >
                              {errorText(customerError(field))}
                            </p>
                          )}
                        </div>
                      ),
                    )}
                  </FormFields>
                  {form.data.customer_id && (
                    <p className="text-muted-foreground text-sm">
                      {t("requests.saved_customer_help")}
                    </p>
                  )}
                </FieldSet>
                <Separator />
                <FieldSet disabled={needsAssociation}>
                  <FieldLegend variant="section">
                    {t("vehicles.details")}
                  </FieldLegend>
                  {needsAssociation && (
                    <p className="text-sm text-muted-foreground">
                      {t("requests.associate_first")}
                    </p>
                  )}
                  {selectedCustomer && (
                    <>
                      <div className="flex flex-wrap items-end gap-3">
                        <div className="min-w-0 flex-1 space-y-2">
                          <Label htmlFor="request-vehicle-search">
                            {t("requests.search_vehicles")}
                          </Label>
                          <Input
                            id="request-vehicle-search"
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                if (!event.nativeEvent.isComposing)
                                  navigate({ vehicle_page: 1 });
                              }
                            }}
                            value={vehicleQuery}
                            onChange={(event) =>
                              setVehicleQuery(event.target.value)
                            }
                          />
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => navigate({ vehicle_page: 1 })}
                        >
                          {t("requests.filter")}
                        </Button>
                      </div>
                      <Choice
                        id="request-vehicle-choice"
                        name="vehicle_selection"
                        label={t("requests.choose_vehicle")}
                        value={selectedVehicle?.id || "new"}
                        onValueChange={(value) => {
                          const chosen = vehicles.find(
                            (choice) => choice.id === value,
                          );
                          setData((data) => ({
                            ...data,
                            vehicle_id: chosen?.id || "",
                            vehicle_revision: chosen?.revision || "",
                          }));
                        }}
                        options={[
                          { id: "new", name: t("requests.new_vehicle") },
                          ...vehicles.map((choice) => ({
                            id: choice.id,
                            name: `${choice.make} ${choice.model}${choice.registration_number ? ` · ${choice.registration_number}` : ""}`,
                          })),
                        ]}
                      />
                      {pagination("vehicle", vehicle_pagination)}
                    </>
                  )}
                  {!selectedVehicle && (
                    <VehicleFields
                      values={form.data as VehicleValues}
                      catalogue={catalogue}
                      inputPrefix="request[vehicle]"
                      idPrefix="request"
                      autoFocus={false}
                      required={false}
                      errors={vehicleErrors}
                      onChange={(field, value) => setData(field, value)}
                    />
                  )}
                  <VehiclePhotos
                    name="request[vehicle][files][]"
                    id="request-files"
                    files={form.data.files}
                    onChange={(files) => setData("files", files)}
                    existingPhotos={enquiry?.vehicle_photos || []}
                    retainedName="request[vehicle][photo_ids][]"
                    removedIds={(enquiry?.vehicle_photos || [])
                      .filter(
                        (photo) => !form.data.photo_ids.includes(photo.id),
                      )
                      .map((photo) => photo.id)}
                    onRemovedIdsChange={(removed) =>
                      setData(
                        "photo_ids",
                        (enquiry?.vehicle_photos || [])
                          .filter((photo) => !removed.includes(photo.id))
                          .map((photo) => photo.id),
                      )
                    }
                    error={vehicleErrors.files}
                    help={
                      <>
                        {t("requests.saved_photo_help")}{" "}
                        {t("requests.photo_reselect")}
                      </>
                    }
                  />
                  {!selectedVehicle && <CatalogueCredit />}
                </FieldSet>
                <Separator />
                <FieldSet>
                  <FieldLegend variant="section">
                    {t("services.title")}
                  </FieldLegend>
                  <div className="space-y-2">
                    <Label htmlFor="request-service-details">
                      {t("requests.service_details")}
                    </Label>
                    <Textarea
                      id="request-service-details"
                      name="request[service_details]"
                      value={form.data.service_details}
                      onChange={(event) =>
                        setData("service_details", event.target.value)
                      }
                      maxLength={10000}
                      aria-invalid={!!errors.service_details}
                      aria-describedby={
                        errors.service_details
                          ? "request-service-details-error"
                          : undefined
                      }
                    />
                    {errors.service_details && (
                      <p
                        id="request-service-details-error"
                        className="text-sm text-destructive"
                      >
                        {errorText(errors.service_details)}
                      </p>
                    )}
                  </div>
                  {!enquiry && (
                    <>
                      <Choice
                        id="request-channel"
                        name="request[channel]"
                        label={t("requests.channel")}
                        value={form.data.channel}
                        onValueChange={(value) =>
                          setData("channel", value || "manual")
                        }
                        options={[
                          "manual",
                          "phone",
                          "walk_in",
                          "facebook",
                          "instagram",
                          "whatsapp",
                        ].map((id) => ({
                          id,
                          name: t(`requests.channels.${id}`),
                        }))}
                      />
                      <div className="space-y-2">
                        <Label htmlFor="request-identity">
                          {t("requests.channel_identity")}
                        </Label>
                        <Input
                          id="request-identity"
                          name="request[channel_identity]"
                          value={form.data.channel_identity}
                          onChange={(event) =>
                            setData("channel_identity", event.target.value)
                          }
                          maxLength={255}
                          aria-invalid={!!errors.channel_identity}
                          aria-describedby={
                            errors.channel_identity
                              ? "request-identity-error"
                              : undefined
                          }
                        />
                        {errors.channel_identity && (
                          <p
                            id="request-identity-error"
                            className="text-sm text-destructive"
                          >
                            {errorText(errors.channel_identity)}
                          </p>
                        )}
                        <p className="text-muted-foreground text-sm">
                          {t("requests.identity_help")}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="request-body">
                          {t("requests.body")}
                        </Label>
                        <Textarea
                          id="request-body"
                          name="request[body]"
                          value={form.data.body}
                          onChange={(event) =>
                            setData("body", event.target.value)
                          }
                          required
                          maxLength={10000}
                          rows={5}
                          aria-invalid={!!errors.body}
                          aria-describedby={
                            errors.body ? "request-body-error" : undefined
                          }
                        />
                        {errors.body && (
                          <p
                            id="request-body-error"
                            className="text-sm text-destructive"
                          >
                            {errorText(errors.body)}
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </FieldSet>
                <div className="flex flex-wrap gap-3">
                  <Button type="submit" disabled={form.processing}>
                    {t(
                      needsAssociation
                        ? "requests.use_customer"
                        : enquiry
                          ? "requests.save_details"
                          : "requests.save",
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    render={
                      <Link
                        href={backPath}
                        onClick={() => {
                          completed.current = true;
                          forgetEnquiryDraft(key);
                          form.resetAndClearErrors();
                        }}
                      />
                    }
                  >
                    {t("customers.cancel")}
                  </Button>
                </div>
              </form>
            </FormContent>
          </CardContent>
        </Card>
      </PageCanvas>
    </AppLayout>
  );
}
